// Grabs a few still frames from a video FILE entirely in the browser, so
// the AI can "watch" what the worker is asking about without the video
// ever being uploaded or stored on the server — keeps storage empty and
// the system fast. Returns an array of JPEG data URLs (same shape as the
// photo attachments the assistant already sends).

function grabAt(video, ctx, canvas, time, quality) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (val) => {
      if (done) return;
      done = true;
      video.removeEventListener("seeked", onSeeked);
      resolve(val);
    };
    const onSeeked = () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL("image/jpeg", quality));
      } catch {
        finish(null);
      }
    };
    video.addEventListener("seeked", onSeeked);
    // Some decoders never fire "seeked" for a bad timestamp — bail after 3s.
    setTimeout(() => finish(null), 3000);
    try {
      video.currentTime = Math.max(0, time);
    } catch {
      finish(null);
    }
  });
}

export async function extractVideoFrames(file, { maxFrames = 4, maxDim = 1024, quality = 0.7 } = {}) {
  if (!file || !file.type?.startsWith("video/")) return [];
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    let settled = false;
    const finish = (frames) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(frames);
    };

    video.onerror = () => finish([]);
    // Safety net if metadata never loads.
    setTimeout(() => finish([]), 15000);

    video.onloadedmetadata = async () => {
      try {
        const duration = isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
        const times = [];
        if (duration > 0) {
          for (let i = 1; i <= maxFrames; i++) times.push((duration * i) / (maxFrames + 1));
        } else {
          times.push(0);
        }
        const w = video.videoWidth || maxDim;
        const h = video.videoHeight || maxDim;
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext("2d");

        const frames = [];
        for (const t of times) {
          const frame = await grabAt(video, ctx, canvas, t, quality);
          if (frame) frames.push(frame);
        }
        finish(frames);
      } catch {
        finish([]);
      }
    };
  });
}

// Snapshot the frame currently showing in a <video> element (paused at a
// scrubbed position) as a downscaled JPEG data URL. Nothing is uploaded.
export function captureFrame(video, { maxDim = 1024, quality = 0.7 } = {}) {
  const w = video?.videoWidth || 0;
  const h = video?.videoHeight || 0;
  if (!w || !h) return null;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  try {
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return null;
  }
}
